import { type FC, useEffect } from "react";
import { type IntlShape, useIntl } from "react-intl";
import { useToken } from "@packsize/authentication";
import { type Workstation, type WorkstationChanged } from "@packsize/schemas/machines";

import { baseRoutes } from "@/api/base-routes";
import { useSignalRHub } from "@/hooks/use-signalr-hub";
import type { ErrorsAndWarningsProps, MachineFaultDisplay, MachineFaultType } from "@/schemas";

const websocketUrl = `${baseRoutes.cvpRange}/hubs/CVPRange`;
const errorsChangedEventName = "OnWorkstationStopReasonChanged";
const warningsChangedEventName = "OnWorkstationWarningsChanged";

function createDisplayItems(
	machineId: string,
	data: number[] | null | undefined,
	severity: MachineFaultType,
	intl: IntlShape,
): MachineFaultDisplay[] {
	if (data === undefined || data === null) {
		return [];
	}

	return data.map((code) => {
		const translationKey = `CVPRange ${severity} ${code}`;
		const actionKey = `CVPRange ${severity} ${code} Action`;
		const translatedAction = intl.formatMessage({ id: actionKey, defaultMessage: "{actionKey}" }, { actionKey });
		const resolution = actionKey === translatedAction ? undefined : translatedAction;
		return {
			machineId,
			title: intl.formatMessage({ id: translationKey, defaultMessage: "{translationKey}" }, { translationKey }),
			resolution: resolution,
			image: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/cvprange/${severity.toLowerCase()}/${code}.svg`,
			fallbackImage: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/cvprange/${severity.toLowerCase()}/default.svg`,
			type: severity,
		};
	});
}

const getErrorCodes = (workstations: Workstation[]) => {
	const codes = [];
	for (let i = 0; i < workstations.length; i++) {
		const stopReason = workstations[i].stopReason;
		if (stopReason !== null && stopReason.isError) {
			codes.push(stopReason.code);
		}
	}

	return Array.from(new Set(codes));
};

const getWarningCodes = (workstations: Workstation[]) => {
	const codes = [];
	for (let i = 0; i < workstations.length; i++) {
		const warnings = workstations[i].warnings;
		if (warnings !== null) {
			codes.push(...warnings.map((warning) => warning.code));
		}
	}
	return Array.from(new Set(codes));
};

interface CVPRangeMachineErrorsAndWarningsProps {
	readonly machineId: string;
	readonly onErrorsChanged: ErrorsAndWarningsProps["onErrorsChanged"];
	readonly onWarningsChanged: NonNullable<ErrorsAndWarningsProps["onWarningsChanged"]>;
}

const CVPRangeMachineErrorsAndWarnings = ({
	machineId,
	onErrorsChanged,
	onWarningsChanged,
}: CVPRangeMachineErrorsAndWarningsProps) => {
	const intl = useIntl();
	const { bearerToken } = useToken();
	const { hubConnection, hubConnectionEstablished, invoke, subscribe, unsubscribe } = useSignalRHub({
		hubUrl: websocketUrl,
		token: bearerToken,
	});

	useEffect(() => {
		if (hubConnectionEstablished === false) {
			return;
		}

		void invoke("RegisterMachine", machineId);
	}, [hubConnectionEstablished, intl, invoke, machineId, onErrorsChanged, onWarningsChanged]);

	useEffect(() => {
		if (hubConnection === undefined) {
			return;
		}

		const handleErrorsChanged = (...args: unknown[]) => {
			const [changeEvent] = args as [WorkstationChanged];

			if (changeEvent.machineId !== machineId) {
				return;
			}
			const codes = getErrorCodes(changeEvent.workstations);
			onErrorsChanged(machineId, createDisplayItems(machineId, codes, "Error", intl));
		};

		subscribe(errorsChangedEventName, handleErrorsChanged);

		return () => {
			unsubscribe(errorsChangedEventName, handleErrorsChanged);
		};
	}, [hubConnection, intl, machineId, onErrorsChanged, subscribe, unsubscribe]);

	useEffect(() => {
		if (hubConnection === undefined) {
			return;
		}

		const handleWarningsChanged = (...args: unknown[]) => {
			const [changeEvent] = args as [WorkstationChanged];

			if (changeEvent.machineId !== machineId) {
				return;
			}
			const codes = getWarningCodes(changeEvent.workstations);
			onWarningsChanged(machineId, createDisplayItems(machineId, codes, "Warning", intl));
		};

		subscribe(warningsChangedEventName, handleWarningsChanged);

		return () => {
			unsubscribe(warningsChangedEventName, handleWarningsChanged);
		};
	}, [hubConnection, intl, machineId, onWarningsChanged, subscribe, unsubscribe]);

	return null;
};

export const CVPRangeErrorsAndWarnings: FC<Required<ErrorsAndWarningsProps>> = ({
	machines,
	onErrorsChanged,
	onWarningsChanged,
}) => {
	return (
		<>
			{machines
				.filter(({ machineType }) => machineType === "CVPRange")
				.map(({ machineId }) => (
					<CVPRangeMachineErrorsAndWarnings
						key={machineId}
						machineId={machineId}
						onErrorsChanged={onErrorsChanged}
						onWarningsChanged={onWarningsChanged}
					/>
				))}
		</>
	);
};
